import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  SearchablePopover,
  SearchableSelect,
  type SearchableSelectOption,
  DatePicker,
  Separator,
} from "@racoon/ui";
import { ChevronDown } from "lucide-react";
import { IcCalendar, IcCar, IcFilter } from "@racoon/icons";
import { ConnectProto } from "@racoon/api";
import { useT, Namespace, useTypedT } from "@racoon/i18n";
import { isTruckFree } from "../../utils/route-calculations";
import { humanTruckType } from "~/pages/dashboard/autopark/helpers";

interface RouteSidebarProps {
  selectedDate?: Date;
  onDateChange: (date: Date | undefined) => void;
  /** Latest selectable shift date — a pseudo-route only records past work. */
  maxDate?: Date;

  selectedTruck?: ConnectProto.CurrentTruckStatus;
  isTruckPopoverOpen: boolean;
  onTruckPopoverChange: (open: boolean) => void;
  truckSearchQuery: string;
  onTruckSearchQueryChange: (value: string) => void;
  filteredTrucks: ConnectProto.CurrentTruckStatus[];
  // Every truck of the shift, before the plate/driver search narrows it down.
  // Only the filter dropdowns read it, so their options stay stable while the
  // operator types in the plate search.
  allTrucks?: ConnectProto.CurrentTruckStatus[];
  onTruckSelect: (truckId: string) => void;
}

// The three sidebar filters, keyed the way they are read off a truck.
const truckFilterValues = (item: ConnectProto.CurrentTruckStatus) => ({
  type:
    item.truck?.type != null && item.truck.type !== ConnectProto.TruckType.UNDEFINED
      ? String(item.truck.type)
      : "",
  category: item.truck?.truckType?.name ?? "",
  subOrg: item.truck?.ownerOrganization?.name ?? "",
});

export const RouteSidebar: React.FC<RouteSidebarProps> = ({
  selectedDate,
  onDateChange,
  maxDate,
  selectedTruck,
  isTruckPopoverOpen,
  onTruckPopoverChange,
  truckSearchQuery,
  onTruckSearchQueryChange,
  filteredTrucks,
  allTrucks,
  onTruckSelect,
}) => {
  const t = useTypedT(Namespace.Routes);
  const tAny = useT();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [filterType, setFilterType] = useState<string>("");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [filterSubOrg, setFilterSubOrg] = useState<string>("");

  const activeFilterCount = [filterType, filterCategory, filterSubOrg].filter(Boolean).length;

  const truck = selectedTruck?.truck;

  const optionSource = allTrucks ?? filteredTrucks;

  // Each dropdown lists what is reachable given the *other* two selections, so a
  // combination that matches nothing can't be assembled. Its own value is never
  // part of the predicate, so the current selection always stays listed.
  const optionsFor = useCallback(
    (
      field: "type" | "category" | "subOrg",
      label: (item: ConnectProto.CurrentTruckStatus, value: string) => string,
    ) => {
      const map = new Map<string, string>();
      optionSource.forEach((item) => {
        const values = truckFilterValues(item);
        if (field !== "type" && filterType && values.type !== filterType) return;
        if (field !== "category" && filterCategory && values.category !== filterCategory) return;
        if (field !== "subOrg" && filterSubOrg && values.subOrg !== filterSubOrg) return;

        const value = values[field];
        if (value) map.set(value, label(item, value));
      });
      return Array.from(map, ([id, name]): SearchableSelectOption => ({ id, name }));
    },
    [optionSource, filterType, filterCategory, filterSubOrg],
  );

  const typeOptions = useMemo(
    () => optionsFor("type", (item) => humanTruckType(item.truck?.type, tAny)),
    [optionsFor, tAny],
  );

  const categoryOptions = useMemo(
    () => optionsFor("category", (_item, value) => value),
    [optionsFor],
  );

  const subOrgOptions = useMemo(() => optionsFor("subOrg", (_item, value) => value), [optionsFor]);

  // What the plate-number popover actually lists: the search result narrowed by
  // the active filters.
  const visibleTrucks = useMemo(() => {
    if (activeFilterCount === 0) return filteredTrucks;
    return filteredTrucks.filter((item) => {
      const values = truckFilterValues(item);
      return (
        (!filterType || values.type === filterType) &&
        (!filterCategory || values.category === filterCategory) &&
        (!filterSubOrg || values.subOrg === filterSubOrg)
      );
    });
  }, [filteredTrucks, activeFilterCount, filterType, filterCategory, filterSubOrg]);

  const resetFilters = () => {
    setFilterType("");
    setFilterCategory("");
    setFilterSubOrg("");
  };

  const handleSelectTruck = (truckId: string) => {
    onTruckSelect(truckId);
    onTruckSearchQueryChange("");
    onTruckPopoverChange(false);
  };

  return (
    <div className="flex w-[220px] shrink-0 flex-col overflow-y-auto rounded-xl border border-gray-200 bg-white">
      {/* Shift date */}
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-2">
          <IcCalendar className="h-5 w-5 text-brand-blue" />
          <span className="text-sm font-medium text-text-primary">{t("shiftDate")}</span>
        </div>
        <DatePicker
          value={selectedDate}
          onChange={onDateChange}
          maxDate={maxDate}
          hideIcon
          className="h-8 w-full justify-start rounded-lg border border-gray-200 px-3 text-xs"
        />
      </div>

      <Separator />

      {/* Transport */}
      <div className="flex flex-col">
        <div className="flex items-center gap-2 p-4">
          <IcCar className="h-5 w-5 text-brand-blue" />
          <span className="text-sm font-medium text-text-primary">{t("transport")}</span>
        </div>

        {/* Filter */}
        <button
          type="button"
          onClick={() => setIsFilterOpen((prev) => !prev)}
          className="flex w-full items-center justify-between px-4 pb-4"
        >
          <span className="flex items-center gap-2">
            <IcFilter
              className={`h-6 w-6 ${
                isFilterOpen || activeFilterCount > 0 ? "text-brand-blue" : "text-text-secondary"
              }`}
            />
            <span
              className={`text-xs font-medium ${
                isFilterOpen || activeFilterCount > 0 ? "text-brand-blue" : "text-text-secondary"
              }`}
            >
              {t("filterBy")}
            </span>
            {/* The panel collapses, so an active filter has to stay visible from
                the header — otherwise a short truck list looks like a bug. */}
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-brand-blue px-1.5 text-[10px] font-semibold leading-4 text-white">
                {activeFilterCount}
              </span>
            )}
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${
              isFilterOpen ? "rotate-180 text-brand-blue" : "text-text-secondary"
            }`}
          />
        </button>

        {isFilterOpen && (
          <div className="flex flex-col gap-4 px-4 pb-4">
            <FilterSelect
              inputId="assign-route-filter-type-input"
              placeholder={tAny(Namespace.Common, "select")}
              label={t("truckCategoryFilter")}
              value={filterType}
              onChange={setFilterType}
              options={typeOptions}
            />
            <FilterSelect
              inputId="assign-route-filter-category-input"
              label={t("truckTypeFilter")}
              placeholder={tAny(Namespace.Common, "select")}
              value={filterCategory}
              onChange={setFilterCategory}
              options={categoryOptions}
            />
            <FilterSelect
              inputId="assign-route-filter-suborg-input"
              label={t("subOrganization")}
              placeholder={tAny(Namespace.Common, "select")}
              value={filterSubOrg}
              onChange={setFilterSubOrg}
              options={subOrgOptions}
            />
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={resetFilters}
                className="self-start text-xs font-medium text-brand-blue hover:underline"
              >
                {tAny(Namespace.Common, "reset")}
              </button>
            )}
          </div>
        )}

        {/* Plate number */}
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-primary">{t("plateNumberLabel")}</span>
            <SearchablePopover
              open={isTruckPopoverOpen}
              onOpenChange={(open) => {
                onTruckPopoverChange(open);
                if (!open) {
                  onTruckSearchQueryChange("");
                }
              }}
              trigger={
                <Button
                  variant="outline"
                  className="flex h-8 w-full items-center justify-between rounded-lg border border-gray-200 !px-2"
                >
                  <span className="text-sm text-text-primary">
                    {truck?.plateNumber || t("selectTruck")}
                  </span>
                  <ChevronDown className="h-4 w-4 text-text-primary" />
                </Button>
              }
              searchValue={truckSearchQuery}
              onSearchChange={onTruckSearchQueryChange}
              inputId="assign-route-truck-search-input"
              placeholder={t("searchTruckOrDriverPlaceholder")}
              contentClassName="w-72 !p-0"
              listClassName="max-h-60 overflow-y-auto"
              contentProps={{}}
            >
              {visibleTrucks.map((item) => {
                const free = isTruckFree(item);
                const truckIdStr = item.truck?.id?.toString() || "";
                return (
                  <button
                    key={truckIdStr}
                    onClick={() => handleSelectTruck(truckIdStr)}
                    className="w-full border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                  >
                    <div className="flex flex-row justify-between">
                      <span
                        className={`text-sm font-semibold ${
                          free ? "text-brand-blue" : "text-gray-900"
                        }`}
                      >
                        {item.truck?.plateNumber}
                      </span>
                      <div className={`text-xs ${free ? "text-brand-blue" : "text-gray-500"}`}>
                        {!item.currentShift
                          ? t("noShift")
                          : item.currentShift.driver
                            ? `${item.currentShift.driver.firstName} ${item.currentShift.driver.lastName}`
                            : t("withoutDriver")}
                      </div>
                    </div>
                  </button>
                );
              })}

              {visibleTrucks.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  {truckSearchQuery.trim() || activeFilterCount > 0
                    ? t("trucksNotFound")
                    : t("noTrucksAvailable")}
                </div>
              )}
            </SearchablePopover>
          </div>

          {/* Truck details */}
          {truck && (
            <div className="flex flex-col gap-2 text-sm">
              {truck.model && <span className="text-text-primary">{truck.model}</span>}
              <span className="text-text-secondary">{humanTruckType(truck.type, tAny)}</span>
              {truck.ownerOrganization?.name && (
                <span className="text-text-secondary">{truck.ownerOrganization.name}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface FilterSelectProps {
  inputId: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
}

const FilterSelect: React.FC<FilterSelectProps> = ({
  inputId,
  label,
  placeholder,
  value,
  onChange,
  options,
}) => {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-primary">{label}</span>
      <SearchableSelect
        id={inputId}
        value={value}
        options={options}
        // Re-picking the active option clears it — the only way back to "no filter"
        // from within the list.
        onChange={(next) => onChange(next === value ? "" : next)}
        placeholder={placeholder}
        size="sm"
        contentClassName="w-[188px]"
      />
    </div>
  );
};
